import { Component, OnInit } from '@angular/core';
import { Usuario } from '../../core/models/usuario';
import { UsuarioService } from 'src/app/core/services/usuario.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-usuario',
  templateUrl: './usuario.component.html',
  styleUrls: ['./usuario.component.scss'],
})
export class UsuarioComponent implements OnInit {
  usuario = new Usuario();
  usuarios: Usuario[] = [];

  // Opções de roles disponíveis
  rolesOptions: string[] = ['ADMIN', 'USER'];

  // estado de submissão para desabilitar botão e mostrar feedback
  isSubmitting = false;

  // edição
  editMode = false;
  editUsuarioId: number | null = null;

  // controle de visibilidade da senha no formulário
  showPassword = false;

  constructor(
    private readonly servico: UsuarioService,
    private readonly auth: AuthService,
    private readonly toast: ToastService,
    private readonly confirmation: ConfirmationService
  ) {}

  // conveniência para habilitar botão
  get canSubmit(): boolean {
    return !!(this.usuario && (this.usuario.username || '').toString().trim() && (this.usuario.password || '').toString().trim());
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  selecionar(): void {
    this.servico.selecionar().subscribe((retorno) => {
      this.usuarios = retorno;
    });
  }

  // Alterna seleção de role (checkbox)
  toggleRole(role: string): void {
    this.usuario.roles ??= [];
    const idx = this.usuario.roles.indexOf(role);
    if (idx >= 0) {
      this.usuario.roles.splice(idx, 1);
    } else {
      this.usuario.roles.push(role);
    }
  }

  // Inicia edição, popula o formulário
  editar(u: Usuario): void {
    this.editMode = true;
    this.editUsuarioId = u.id ?? null;
    this.usuario = { ...u } as Usuario; // copia valores
  }

  cancelarEdicao(): void {
    this.editMode = false;
    this.editUsuarioId = null;
    this.usuario = new Usuario();
  }

  limparFormulario(): void {
    this.usuario = new Usuario();
    this.usuario.empresaId = this.auth.getEmpresaId() || undefined;
    this.editMode = false;
    this.editUsuarioId = null;
  }

  // Salva (cria ou altera dependendo do editMode)
  salvar(): void {
    if (!this.canSubmit) {
      this.toast.warn('Preencha usuário e senha antes de salvar.');
      return;
    }

    const empresaId = this.auth.getEmpresaId();
    if (!empresaId) {
      this.toast.error('Empresa (tenant) não encontrado. Faça login novamente.');
      return;
    }

    this.usuario.empresaId = empresaId;
    this.usuario.roles ??= [];
    if (typeof (this.usuario.roles as any) === 'string') {
      this.usuario.roles = [(this.usuario.roles as any)];
    }
    this.usuario.roles = this.usuario.roles.map((r) => (r || '').toString().trim()).filter((r) => r.length > 0);

    this.isSubmitting = true;

    if (this.editMode && this.editUsuarioId != null) {
      this.servico.alterar(this.editUsuarioId, this.usuario).subscribe({
        next: (retorno) => {
          const idx = this.usuarios.findIndex((x) => x.id === this.editUsuarioId);
          if (idx >= 0) this.usuarios[idx] = retorno;
          this.toast.success('Usuário alterado com sucesso');
          this.cancelarEdicao();
          this.isSubmitting = false;
        },
        error: (err) => {
          const msg = err?.error?.message || err?.message || 'Erro ao alterar usuário';
          this.toast.error(msg);
          console.error('Erro ao alterar usuário:', err);
          this.isSubmitting = false;
        },
      });
    } else {
      // criar novo
      this.servico.cadastrar(this.usuario).subscribe({
        next: (retorno) => {
          this.usuarios.push(retorno);
          this.usuario = new Usuario();
          this.toast.success('Usuário cadastrado com sucesso');
          this.isSubmitting = false;
        },
        error: (err) => {
          const msg = err?.error?.message || err?.message || 'Erro ao cadastrar usuário';
          this.toast.error(msg);
          console.error('Erro ao cadastrar usuário:', err);
          this.isSubmitting = false;
        },
      });
    }
  }

  confirmarRemover(usuario: Usuario | undefined): void {
    if (!usuario || !usuario.id) return;

    const willDeactivate = !!usuario.ativo; // se atualmente ativo -> vamos desativar
    const actionLabel = willDeactivate ? 'desativar' : 'ativar';

    this.confirmation.confirm({
      message: `Confirma ${actionLabel} o usuário ${usuario.username || usuario.id}?`,
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => {
        // chama o endpoint de desativação/ativação
        this.servico.desativar(usuario.id as number, !willDeactivate).subscribe({
          next: (retorno) => {
            // atualiza item na lista com dados retornados (ou faz fallback local)
            const idx = this.usuarios.findIndex((u) => u.id === usuario.id);
            if (idx >= 0) {
              if (retorno) {
                this.usuarios[idx] = retorno;
              } else {
                // fallback: apenas inverte o campo ativo localmente
                this.usuarios[idx].ativo = !willDeactivate;
              }
            }
            this.toast.success(`Usuário ${willDeactivate ? 'desativado' : 'ativado'} com sucesso`);
          },
          error: (err) => {
            const msg = err?.error?.message || err?.message || `Erro ao ${actionLabel} usuário`;
            this.toast.error(msg);
            console.error(`Erro ao ${actionLabel} usuário:`, err);
          },
        });
      },
    });
  }

  //Método de inicialização
  ngOnInit(): void {
    // Obtém empresaId do token para pré-preencher novos usuários
    const empresaId = this.auth.getEmpresaId();
    if (empresaId) {
      this.usuario.empresaId = empresaId;
    }
    this.selecionar();
  }
}
